import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser } from '@clerk/clerk-react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, Upload, FileText, Clock, RefreshCw } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface Lecture {
  id: string;
  title: string;
  version: string;
  lastUpdated: string;
  embedUrl: string;
}

const LectureManager = () => {
  const [, navigate] = useLocation();
  const { user } = useUser();
  const { toast } = useToast();
  
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingLectureId, setUploadingLectureId] = useState('');
  const [uploadingTitle, setUploadingTitle] = useState('');
  const [uploadingHTML, setUploadingHTML] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Check if user is authorized
  const isAuthorized = user?.emailAddresses?.[0]?.emailAddress === 'anant@nextgenerationmedicine.co';

  useEffect(() => {
    if (isAuthorized) {
      fetchLectures();
    }
  }, [isAuthorized]);

  const fetchLectures = async () => {
    try {
      const response = await apiRequest('GET', '/api/lectures');
      const data = await response.json();
      setLectures(data.lectures || []);
    } catch (error) {
      console.error('Failed to fetch lectures:', error);
      toast({
        title: "Error",
        description: "Failed to load lectures",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadingLectureId || !uploadingTitle || !uploadingHTML) {
      toast({
        title: "Missing Information",
        description: "Please provide lecture ID, title, and HTML content",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);
    try {
      const response = await apiRequest('POST', '/api/lectures/store', {
        lectureId: uploadingLectureId,
        title: uploadingTitle,
        htmlContent: uploadingHTML
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: `Lecture uploaded successfully. Version: ${data.version}`
        });
        
        // Clear form
        setUploadingLectureId('');
        setUploadingTitle('');
        setUploadingHTML('');
        
        // Refresh lectures list
        fetchLectures();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload lecture content",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthorized) {
    return (
      <MentorshipAccessGate>
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h1>
              <p className="text-gray-600">This tool is only available to anant@nextgenerationmedicine.co</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/mentorship")}
              >
                Back to Mentorship
              </Button>
            </div>
          </div>
          <Footer />
        </div>
      </MentorshipAccessGate>
    );
  }

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
            
            <Button
              variant="outline"
              onClick={fetchLectures}
              disabled={isLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Lecture Manager</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Lecture Manager
            </h1>
            <p className="text-[#7F8C8D] mb-8">
              Manage embedded HTML lectures, track versions, and upload updates.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Upload Lecture HTML
                  </CardTitle>
                  <CardDescription>
                    Upload generated HTML content from the lecture generator
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="lectureId">Lecture ID</Label>
                    <Input
                      id="lectureId"
                      value={uploadingLectureId}
                      onChange={(e) => setUploadingLectureId(e.target.value)}
                      placeholder="e.g., testosterone-molecular-foundations"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="title">Lecture Title</Label>
                    <Input
                      id="title"
                      value={uploadingTitle}
                      onChange={(e) => setUploadingTitle(e.target.value)}
                      placeholder="e.g., Testosterone: Molecular Foundations"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="htmlContent">HTML Content</Label>
                    <Textarea
                      id="htmlContent"
                      value={uploadingHTML}
                      onChange={(e) => setUploadingHTML(e.target.value)}
                      placeholder="Paste the generated HTML content here..."
                      className="min-h-[200px] font-mono text-xs"
                    />
                  </div>
                  
                  <Button 
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="w-full"
                  >
                    {isUploading ? 'Uploading...' : 'Upload HTML'}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Lectures List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Uploaded Lectures
                  </CardTitle>
                  <CardDescription>
                    Manage and track versions of uploaded lectures
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <p className="text-center text-gray-500 py-8">Loading lectures...</p>
                  ) : lectures.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No lectures uploaded yet</p>
                  ) : (
                    <div className="space-y-4">
                      {lectures.map((lecture) => (
                        <div
                          key={lecture.id}
                          className="border rounded-lg p-4 space-y-2"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium">{lecture.title}</h4>
                              <p className="text-sm text-gray-500">ID: {lecture.id}</p>
                            </div>
                            <Badge variant="outline">v{lecture.version}</Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(lecture.lastUpdated)}</span>
                          </div>
                          
                          <div className="text-sm">
                            <span className="text-gray-600">Embed URL: </span>
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                              {lecture.embedUrl}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Instructions */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>How to Use</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-2">
                <p><strong>1.</strong> Generate HTML content using the <Link href="/mentorship/lecture-generator" className="text-blue-600 hover:underline">Lecture Generator</Link></p>
                <p><strong>2.</strong> Copy the generated HTML and paste it in the upload form</p>
                <p><strong>3.</strong> Use a consistent lecture ID (e.g., "testosterone-molecular-foundations")</p>
                <p><strong>4.</strong> The system will automatically version your uploads</p>
                <p><strong>5.</strong> Use the embed URL in lecture components with the EmbeddedLecture component</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LectureManager; 